'use client';

import { type ComponentProps, type ReactNode, useCallback } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { Pagination } from '@zillow/constellation-10';

// Constellation 10.14's renderItem callback declares `buttonProps` as
// `Partial<PaginationPageButtonPropsInterface>`, but its JSX-type inference
// for `<Pagination.PageButton>` narrows to plain `RefAttributes` when a spread
// is involved — making `<Pagination.PageButton {...buttonProps} />` error
// even though the destination component does accept those props at runtime.
// 10.17 fixes the inference. Cast to the component's `ComponentProps` so the
// spread satisfies the narrow inferred type without `@ts-expect-error`.
type PageButtonProps = ComponentProps<typeof Pagination.PageButton>;
import { css } from '@/styled-system/css';

import type { SearchListingsRequest, SearchListingsResult } from '@/src/lib/api';
import { Link as NextLink } from '@/src/i18n/navigation';
import { LISTINGS_PER_PAGE } from '@/src/lib/constants';
import { useSearchListings } from '@/src/lib/hooks/query';
import { useIsDesktop } from '@/src/lib/hooks/ui/useIsDesktop';
import { emitTrackNextClickExplorePageEvent } from '@/src/analytics/hotpads/events';
import { buildSrpPaginationHref } from '@/src/lib/navigation/srpQueryCarryover';
import { getSrpPageFromUrl, type QueryValue } from '@/src/lib/seo/srp-pagination';
import { extractV3PaginationCounts } from '@/src/lib/utils/v3Listings';
import { useSrpLiveSearchRequest } from './useSrpLiveSearchRequest';
import { useSrpViewMode } from './ViewModeContext';

const MOBILE_PAGE_BUTTON_COUNT = 3;

type Props = {
  breadcrumbs?: ReactNode;
  currentPage: number;
  initialListingsData: SearchListingsResult | null;
  initialParams: SearchListingsRequest;
  areaId: string;
  itemType: string;
  pathname: string;
  paginationQuery: Record<string, QueryValue>;
};

// Fallback for browsers without `inert`, which does the real work of keeping stale
// page links unreachable while the block holds its box.
const inertPagination = css({ pointerEvents: 'none' });

const summaryStyle = css({
  fontSize: 'body-sm',
  fontWeight: 'regular',
  color: 'text.subtle',
  textAlign: 'center',
  marginTop: 'tightest',
});

function getMobilePageNumbers(currentPage: number, totalPages: number): Array<number> {
  if (totalPages <= 0) return [];

  const visibleCount = Math.min(MOBILE_PAGE_BUTTON_COUNT, totalPages);
  const boundedCurrentPage = Math.min(Math.max(currentPage, 1), totalPages);
  const pagesBeforeCurrent = Math.floor(visibleCount / 2);
  let firstPage = Math.max(1, boundedCurrentPage - pagesBeforeCurrent);
  let lastPage = firstPage + visibleCount - 1;

  if (lastPage > totalPages) {
    lastPage = totalPages;
    firstPage = Math.max(1, lastPage - visibleCount + 1);
  }

  return Array.from({ length: lastPage - firstPage + 1 }, (_, index) => firstPage + index);
}

// Scroll the nearest scrollable ancestor (e.g., the content panel) to top on page change
function scrollNearestScrollableToTop(el: HTMLElement | null) {
  while (el) {
    if (el.scrollHeight > el.clientHeight && getComputedStyle(el).overflowY !== 'visible') {
      el.scrollTo({ top: 0 });
      return;
    }
    el = el.parentElement;
  }
  window.scrollTo({ top: 0 });
}

/**
 * SRP footer pagination. Filter/map changes update the URL via pushState (no
 * server rerender), so the totals can't be static SSR props — this reads the
 * same live V3 search query LiveListingsGrid drives and derives page counts
 * from its result (HP-8594).
 */
export function ListingsFooter({
  breadcrumbs,
  currentPage,
  initialListingsData,
  initialParams,
  areaId,
  itemType,
  pathname,
  paginationQuery,
}: Props) {
  const tCommon = useTranslations('common');
  const locale = useLocale();
  const { viewMode } = useSrpViewMode();
  const isDesktop = useIsDesktop();
  const livePathname = usePathname() ?? pathname;
  const searchParams = useSearchParams();
  const resolvedCurrentPage = getSrpPageFromUrl(livePathname, searchParams?.get('page') ?? undefined);

  const liveParams = useSrpLiveSearchRequest({ initialParams, areaId, page: currentPage });
  const isInitialParams = liveParams === initialParams;
  const { data, isPlaceholderData, fetchStatus } = useSearchListings(liveParams, {
    refetchOnMount: false,
    initialData: isInitialParams ? (initialListingsData ?? undefined) : undefined,
  });
  // Render the placeholder result's totals rather than dropping to zero: unmounting
  // the block moved the area content below it twice per map pan (HP-8727). It still
  // unmounts when a search settles at zero, which the reserved empty region absorbs.
  // `fetchStatus` matters: a query disabled for want of a finite bounding box keeps
  // serving placeholder data forever, which would leave the block inert permanently.
  const isPaginationStale = !isInitialParams && isPlaceholderData && fetchStatus !== 'idle';
  const effectiveData = isInitialParams ? (data ?? initialListingsData ?? undefined) : (data ?? undefined);
  const { totalListingsAvailable, totalPages } = extractV3PaginationCounts(effectiveData?.listings, LISTINGS_PER_PAGE);

  const buildPageUrl = useCallback(
    (page: number): string => buildSrpPaginationHref(pathname, page, searchParams, paginationQuery),
    [paginationQuery, pathname, searchParams],
  );

  const handlePageLinkClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    scrollNearestScrollableToTop(e.currentTarget.parentElement);
  }, []);

  const handleNextPageClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      emitTrackNextClickExplorePageEvent();
      handlePageLinkClick(e);
    },
    [handlePageLinkClick],
  );

  if (viewMode === 'feed') return null;

  const formattedCount = totalListingsAvailable.toLocaleString(locale === 'es' ? 'es' : 'en-US');
  const mobilePageNumbers = getMobilePageNumbers(resolvedCurrentPage, totalPages);

  return (
    <>
      {breadcrumbs ? (
        <div
          id="srp-breadcrumbs"
          data-srp-component="breadcrumbs"
          className={css({
            display: 'flex',
            justifyContent: 'center',
            marginTop: 'default',
            marginBottom: 'tight',
          })}
        >
          {breadcrumbs}
        </div>
      ) : null}

      {totalListingsAvailable > 0 && (
        <div
          id="srp-pagination"
          data-srp-component="pagination"
          aria-busy={isPaginationStale ? 'true' : undefined}
          className={isPaginationStale ? inertPagination : undefined}
          {...(isPaginationStale ? { inert: '' } : {})}
        >
          <Pagination.Root
            currentPageIndex={Math.max(0, resolvedCurrentPage - 1)}
            totalPages={totalPages}
            showNumberButtons
            onPageSelected={() => {
              /* no-op: navigation is driven by the rendered <Link> hrefs */
            }}
            aria-label={tCommon('pagination')}
            data-testid="pagination"
          >
            <Pagination.List>
              <Pagination.Item>
                <Pagination.PreviousButton asChild title={tCommon('goToPreviousPage')}>
                  <NextLink
                    href={buildPageUrl(Math.max(1, resolvedCurrentPage - 1))}
                    aria-disabled={resolvedCurrentPage <= 1}
                    tabIndex={resolvedCurrentPage <= 1 ? -1 : 0}
                    onClick={resolvedCurrentPage <= 1 ? undefined : (e) => handlePageLinkClick(e)}
                  />
                </Pagination.PreviousButton>
              </Pagination.Item>
              {isDesktop ? (
                <Pagination.PageItems
                  renderItem={({ buttonProps, key, pageIndex }) => {
                    const pageNum = pageIndex + 1;
                    const isCurrent = pageNum === resolvedCurrentPage;
                    return (
                      <Pagination.Item key={key}>
                        {isCurrent ? (
                          <Pagination.PageButton
                            {...(buttonProps as PageButtonProps)}
                            selected
                            aria-current="page"
                            aria-label={tCommon('goToPage', { page: pageNum })}
                          >
                            {pageNum}
                          </Pagination.PageButton>
                        ) : (
                          <Pagination.PageButton asChild {...(buttonProps as PageButtonProps)} selected={false}>
                            <NextLink
                              href={buildPageUrl(pageNum)}
                              aria-label={tCommon('goToPage', { page: pageNum })}
                              onClick={handlePageLinkClick}
                              prefetch={false}
                            >
                              {pageNum}
                            </NextLink>
                          </Pagination.PageButton>
                        )}
                      </Pagination.Item>
                    );
                  }}
                />
              ) : (
                mobilePageNumbers.map((pageNum) => {
                  const isCurrent = pageNum === resolvedCurrentPage;

                  return (
                    <Pagination.Item key={`mobile-page-${pageNum}`}>
                      {isCurrent ? (
                        <Pagination.PageButton
                          selected
                          aria-current="page"
                          aria-label={tCommon('goToPage', { page: pageNum })}
                        >
                          {pageNum}
                        </Pagination.PageButton>
                      ) : (
                        <Pagination.PageButton asChild selected={false}>
                          <NextLink
                            href={buildPageUrl(pageNum)}
                            aria-label={tCommon('goToPage', { page: pageNum })}
                            onClick={handlePageLinkClick}
                            prefetch={false}
                          >
                            {pageNum}
                          </NextLink>
                        </Pagination.PageButton>
                      )}
                    </Pagination.Item>
                  );
                })
              )}
              <Pagination.Readout>
                {tCommon('pageOf', { current: resolvedCurrentPage, total: totalPages })}
              </Pagination.Readout>
              <Pagination.Item>
                <Pagination.NextButton asChild title={tCommon('goToNextPage')}>
                  <NextLink
                    href={buildPageUrl(Math.min(totalPages, resolvedCurrentPage + 1))}
                    aria-disabled={resolvedCurrentPage >= totalPages}
                    tabIndex={resolvedCurrentPage >= totalPages ? -1 : 0}
                    onClick={resolvedCurrentPage >= totalPages ? undefined : handleNextPageClick}
                  />
                </Pagination.NextButton>
              </Pagination.Item>
            </Pagination.List>
          </Pagination.Root>
          <div className={summaryStyle}>
            {tCommon('pageOf', { current: resolvedCurrentPage, total: totalPages })} • {formattedCount} {itemType}
          </div>
        </div>
      )}
    </>
  );
}
